"use client";

import React, { useState } from "react";
import { 
  LayoutDashboard, 
  Key, 
  BarChart, 
  Settings, 
  HelpCircle, 
  ChevronDown, 
  Bell, 
  Search, 
  Plus 
} from "lucide-react";
import Link from "next/link";
import { motion } from "framer-motion";

type NavItem = {
  label: string;
  href: string;
  icon: React.ElementType;
  active?: boolean;
};

export default function ApiDashboard({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  const navItems: NavItem[] = [
    { label: "Dashboard", href: "/account", icon: LayoutDashboard, active: true },
    { label: "API Keys", href: "/account/api-keys", icon: Key },
    { label: "Usage", href: "/account/usage", icon: BarChart },
    { label: "Settings", href: "/account/settings", icon: Settings },
    { label: "Help", href: "/account/help", icon: HelpCircle }
  ];

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      {/* Sidebar */}
      <motion.aside 
        initial={{ width: sidebarOpen ? 240 : 80 }}
        animate={{ width: sidebarOpen ? 240 : 80 }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
        className="fixed left-0 top-0 z-10 h-full border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-sm"
      >
        <div className="flex h-full flex-col">
          {/* Sidebar header */}
          <div className="flex h-16 items-center justify-between px-4 border-b border-gray-200 dark:border-gray-800">
            <Link href="/account" className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-md bg-blue-600 flex items-center justify-center">
                <span className="text-white font-semibold">K</span>
              </div>
              {sidebarOpen && (
                <motion.span 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                  className="text-lg font-semibold dark:text-white"
                >
                  Kled.io API
                </motion.span>
              )}
            </Link>
            <button 
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="h-8 w-8 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center transition-colors"
            >
              <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${!sidebarOpen ? 'rotate-90' : ''}`} />
            </button>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-3 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.label}
                href={item.href}
                className={`flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-all hover:bg-gray-100 dark:hover:bg-gray-800 ${
                  item.active ? "bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400" : "text-gray-700 dark:text-gray-300"
                }`}
              >
                <item.icon className={`h-5 w-5 flex-shrink-0 ${
                  item.active ? "text-blue-600 dark:text-blue-400" : "text-gray-500 dark:text-gray-400"
                }`} />
                {sidebarOpen && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    {item.label}
                  </motion.span>
                )}
              </Link>
            ))}
          </nav>
          
          {/* User profile */}
          <div className="border-t border-gray-200 dark:border-gray-800 p-3">
            <button className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0 flex items-center justify-center">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-300">JD</span>
              </div>
              {sidebarOpen && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                  className="flex flex-col items-start"
                >
                  <span className="font-medium">John Doe</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">john@example.com</span>
                </motion.div>
              )}
            </button>
          </div>
        </div>
      </motion.aside>

      {/* Main content */}
      <div className={`flex-1 transition-all duration-200 ${sidebarOpen ? 'ml-60' : 'ml-20'}`}>
        {/* Header */}
        <header className="sticky top-0 z-10 h-16 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-sm px-6 flex items-center justify-between">
          <div className="flex items-center gap-3 w-full max-w-md">
            <Search className="h-5 w-5 text-gray-500" />
            <input 
              type="text" 
              placeholder="Search..." 
              className="flex-1 h-9 bg-gray-100 dark:bg-gray-800 border-0 rounded-md px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-shadow"
            />
          </div>
          
          <div className="flex items-center gap-4">
            <button className="h-9 w-9 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center transition-colors relative">
              <Bell className="h-5 w-5 text-gray-500" />
              <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-blue-600"></span>
            </button>
            
            <div className="h-9 w-9 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-300">JD</span>
            </div>
          </div>
        </header>

        {/* Main content area */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
